import React from 'react';
import { Image as ImageIcon, Film, Eraser, Layers, Sparkles, ArrowRight, ShieldCheck, Zap, Star } from 'lucide-react';

interface LandingHubProps {
  onNavigate: (route: 'image' | 'video' | 'bg' | 'batch') => void;
}

export function LandingHub({ onNavigate }: LandingHubProps) {
  return (
    <div className="space-y-12 max-w-6xl mx-auto py-4">
      {/* Hero Header */}
      <div className="text-center space-y-4 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 text-xs font-bold shadow-sm">
          <Sparkles className="w-4 h-4 text-indigo-500 animate-spin" />
          <span>Blivix Creative Suite • 100% In-Browser Privacy</span>
        </div>

        <h1 className="text-4xl sm:text-5xl font-black text-slate-900 dark:text-slate-100 tracking-tight leading-tight">
          What would you like to create today?
        </h1>

        <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
          High-performance studio tools running 100% locally in your browser memory. Zero cloud server uploads, zero quality loss.
        </p>
      </div>

      {/* 4 Primary Suite Cards (Top Artwork Preview + Clean AAA Accessible Typography) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Card 1: Image Studio */}
        <div
          onClick={() => onNavigate('image')}
          className="group relative bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-lg hover:shadow-2xl transition-all cursor-pointer hover:-translate-y-1 overflow-hidden flex flex-col"
        >
          {/* Top Crisp Artwork Preview Window */}
          <div className="relative h-48 w-full overflow-hidden bg-slate-900">
            <img
              src="/card_bgs/img_studio_bg.jpg"
              alt="Image Studio Interface Preview"
              className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
            
            {/* Tool Icon Badge */}
            <div className="absolute top-4 left-4 p-3 bg-gradient-to-tr from-indigo-600 to-purple-600 text-white rounded-2xl shadow-lg shadow-indigo-500/30 group-hover:scale-110 transition-transform">
              <ImageIcon className="w-6 h-6" />
            </div>

            <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-lg bg-black/60 backdrop-blur-md border border-white/20 text-white text-[11px] font-bold">
              Full Workspace Preview
            </div>
          </div>

          {/* Card Body with 100% Clean Typography Contrast */}
          <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
            <div className="space-y-2">
              <h2 className="text-xl font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <span>Image Studio</span>
                <span className="text-xs px-2 py-0.5 rounded-md bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 font-bold">PRO</span>
              </h2>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                Resize, crop, compress, adjust colors, apply watermarks, and preview with interactive split sliders.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-[11px] font-semibold text-slate-600 dark:text-slate-400">
                <span className="bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60">Aspect Presets</span>
                <span className="bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60">Color Filters</span>
                <span className="bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60">Watermarking</span>
              </div>

              <div className="pt-1 flex items-center text-xs font-bold text-indigo-600 dark:text-indigo-400 group-hover:translate-x-1 transition-transform">
                <span>Launch Image Studio</span>
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: AI Background Remover */}
        <div
          onClick={() => onNavigate('bg')}
          className="group relative bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-lg hover:shadow-2xl transition-all cursor-pointer hover:-translate-y-1 overflow-hidden flex flex-col"
        >
          {/* Top Crisp Artwork Preview Window */}
          <div className="relative h-48 w-full overflow-hidden bg-slate-900">
            <img
              src="/card_bgs/bg_remover_bg.jpg"
              alt="AI Background Remover Preview"
              className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
            
            {/* Tool Icon Badge */}
            <div className="absolute top-4 left-4 p-3 bg-gradient-to-tr from-pink-600 to-indigo-600 text-white rounded-2xl shadow-lg shadow-pink-500/30 group-hover:scale-110 transition-transform">
              <Eraser className="w-6 h-6" />
            </div>

            <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-lg bg-black/60 backdrop-blur-md border border-white/20 text-white text-[11px] font-bold">
              AI Subject Masking
            </div>
          </div>

          {/* Card Body with 100% Clean Typography Contrast */}
          <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
            <div className="space-y-2">
              <h2 className="text-xl font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <span>AI Background Remover</span>
              </h2>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                Remove & swap image backdrops, key out white/black/green screens, and use the Magic Restore Brush on details.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-[11px] font-semibold text-slate-600 dark:text-slate-400">
                <span className="bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60">Transparent PNG</span>
                <span className="bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60">Magic Restore Brush</span>
                <span className="bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60">Color Keying</span>
              </div>

              <div className="pt-1 flex items-center text-xs font-bold text-pink-600 dark:text-pink-400 group-hover:translate-x-1 transition-transform">
                <span>Launch Background Remover</span>
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Batch ZIP Processor */}
        <div
          onClick={() => onNavigate('batch')}
          className="group relative bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-lg hover:shadow-2xl transition-all cursor-pointer hover:-translate-y-1 overflow-hidden flex flex-col"
        >
          {/* Top Crisp Artwork Preview Window */}
          <div className="relative h-48 w-full overflow-hidden bg-slate-900">
            <img
              src="/card_bgs/batch_resizer_bg.jpg"
              alt="Batch Multi-Resize Preview"
              className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
            
            {/* Tool Icon Badge */}
            <div className="absolute top-4 left-4 p-3 bg-gradient-to-tr from-emerald-600 to-teal-600 text-white rounded-2xl shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform">
              <Layers className="w-6 h-6" />
            </div>

            <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-lg bg-black/60 backdrop-blur-md border border-white/20 text-white text-[11px] font-bold">
              Data Flow Queue
            </div>
          </div>

          {/* Card Body with 100% Clean Typography Contrast */}
          <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
            <div className="space-y-2">
              <h2 className="text-xl font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <span>Batch Multi-Resize</span>
                <span className="text-xs px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 font-bold">BULK ZIP</span>
              </h2>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                Bulk process dozens of images simultaneously, compress file sizes, and download as a structured ZIP file.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-[11px] font-semibold text-slate-600 dark:text-slate-400">
                <span className="bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60">Bulk Compression</span>
                <span className="bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60">PKZIP Engine</span>
                <span className="bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-200/60 dark:border-slate-700/60">Fast Queue</span>
              </div>

              <div className="pt-1 flex items-center text-xs font-bold text-emerald-600 dark:text-emerald-400 group-hover:translate-x-1 transition-transform">
                <span>Launch Batch Processor</span>
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </div>
            </div>
          </div>
        </div>

        {/* Card 4: Video Studio Pro (Moved to end • Coming Soon) */}
        <div
          className="group relative bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-slate-300/40 dark:border-slate-800/40 shadow-none opacity-80 grayscale hover:grayscale-0 transition-all cursor-not-allowed overflow-hidden flex flex-col select-none"
        >
          {/* Top Crisp Artwork Preview Window */}
          <div className="relative h-48 w-full overflow-hidden bg-slate-900">
            <img
              src="/card_bgs/video_studio_bg.jpg"
              alt="Video Studio Pro Preview"
              className="w-full h-full object-cover object-top opacity-70 transition-all duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
            
            {/* Tool Icon Badge */}
            <div className="absolute top-4 left-4 p-3 bg-slate-700 text-slate-300 rounded-2xl shadow-lg">
              <Film className="w-6 h-6" />
            </div>

            <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-lg bg-amber-500/80 backdrop-blur-md text-white text-[11px] font-bold">
              Multi-Track Timeline
            </div>
          </div>

          {/* Card Body with 100% Clean Typography Contrast */}
          <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
            <div className="space-y-2">
              <h2 className="text-xl font-black text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <span>Video Studio Pro</span>
                <span className="text-xs px-2.5 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/80 text-amber-700 dark:text-amber-400 font-bold border border-amber-300/60 dark:border-amber-800/60">
                  COMING SOON
                </span>
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                Multi-track video cutter, clip splitter, multi-layer background music mixer, Reels resizer & frame grabber.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-[11px] font-semibold text-slate-400 dark:text-slate-600">
                <span className="bg-slate-200 dark:bg-slate-800/50 px-2.5 py-1 rounded-lg">Clip Splitter</span>
                <span className="bg-slate-200 dark:bg-slate-800/50 px-2.5 py-1 rounded-lg">Multi-Audio Layers</span>
                <span className="bg-slate-200 dark:bg-slate-800/50 px-2.5 py-1 rounded-lg">Reels Resizer</span>
              </div>

              <div className="pt-1 flex items-center text-xs font-bold text-amber-600 dark:text-amber-400">
                <span>🚀 Video Studio (Coming Soon)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Guarantee Bar */}
      <div className="p-6 bg-slate-50 dark:bg-slate-900/60 rounded-3xl border border-slate-200/80 dark:border-slate-800 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
        <div className="flex items-center justify-center gap-3">
          <ShieldCheck className="w-6 h-6 text-emerald-500" />
          <div className="text-left">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">100% Local Privacy</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Files never leave your device</p>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3">
          <Zap className="w-6 h-6 text-indigo-500" />
          <div className="text-left">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">Instant Processing</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Powered by browser hardware</p>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3">
          <Star className="w-6 h-6 text-amber-500" />
          <div className="text-left">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">Free Unlimited Exports</h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">No subscriptions or watermarks</p>
          </div>
        </div>
      </div>
    </div>
  );
}
